import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  FormHelperText,
  Alert,
  Divider,
  Grid,
  Select,
  Option
} from '@mui/joy';
import { FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaBuilding, FaSave, FaCreditCard } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { useCustomer } from '../hooks/useCustomer';
import { customerService } from '../services/customerService';
import { useDirectoryOrganisationOptions } from '../hooks/useBrands';
import { isValidVatNumber } from '../services/customerMapping';
import { DIRECTORY_PHONE_DUPLICATE_MESSAGE, isDirectoryPhoneDuplicateError } from '../elevate/customers.phone';

interface CustomerCreationFormProps {
  onCustomerCreated?: (customer: any) => void;
  onCancel?: () => void;
  compact?: boolean;
  initialData?: Partial<{
    title: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    landline_number: string;
    email: string;
    address: string;
    billing_address: string;
    billing_email_address: string;
    billing_contact_name: string;
    payment_preference: string;
    vat_number: string;
    organization_name: string;
  }>;
}

const CustomerCreationForm: React.FC<CustomerCreationFormProps> = ({
  onCustomerCreated,
  onCancel,
  compact = false,
  initialData = {}
}) => {
  const { createQuoteDockCustomer, loading, error } = useCustomer();
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    first_name: initialData.first_name || '',
    last_name: initialData.last_name || '',
    phone_number: initialData.phone_number || '',
    landline_number: initialData.landline_number || '',
    email: initialData.email || '',
    address: initialData.address || '',
    billing_address: initialData.billing_address || '',
    billing_email_address: initialData.billing_email_address || '',
    billing_contact_name: initialData.billing_contact_name || '',
    payment_preference: initialData.payment_preference || '',
    vat_number: initialData.vat_number || '',
    organization_name: initialData.organization_name || (compact ? 'Keystone Glazing' : '')
  });
  // Brands offered for customers (Admin → Organisations), keeping the current one
  const organisationOptions = useDirectoryOrganisationOptions(formData.organization_name);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const controlSize = compact ? 'sm' : undefined;
  const extrasOpen = !compact || showMore;

  // Debounced validation function
  const debouncedValidation = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (field: 'phone_number' | 'email', value: string) => {
        clearTimeout(timeoutId);
        if (!value.trim()) return;
        
        timeoutId = setTimeout(async () => {
          try {
            if (field === 'phone_number') {
              const existingCustomer = await customerService.getCustomerByPhone(value);
              if (existingCustomer) {
                setValidationErrors(prev => ({ 
                  ...prev, 
                  phone_number: DIRECTORY_PHONE_DUPLICATE_MESSAGE 
                }));
              } else {
                setValidationErrors((prev: Record<string, string>) => ({ ...prev, phone_number: '' }));
              }
            } else if (field === 'email') {
              const existingCustomer = await customerService.getCustomerByEmail(value);
              if (existingCustomer) {
                setValidationErrors(prev => ({ 
                  ...prev, 
                  email: 'A customer with this email address already exists' 
                }));
              } else {
                setValidationErrors((prev: Record<string, string>) => ({ ...prev, email: '' }));
              }
            }
          } catch (error) {
            console.error(`Error validating ${field}:`, error);
          }
        }, 500); // 500ms delay
      };
    })(),
    []
  );

  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Require at least first name or last name
    if (!formData.first_name.trim() && !formData.last_name.trim()) {
      errors.first_name = 'First name or last name is required';
    }

    // Phone number validation
    if (!formData.phone_number.trim()) {
      errors.phone_number = 'Phone number is required';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phone_number)) {
      errors.phone_number = 'Please enter a valid phone number';
    }

    // Address validation - ensure it's not null/empty
    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    // Organization validation
    if (!formData.organization_name.trim()) {
      errors.organization_name = 'Organization is required';
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.vat_number.trim() && !isValidVatNumber(formData.vat_number)) {
      errors.vat_number = 'VAT number must be alphanumeric';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (isSubmitting) {
      console.log('Form submission already in progress, ignoring duplicate submit');
      return;
    }
    
    // Prevent submission if there are validation errors
    if (Object.keys(validationErrors).some(key => validationErrors[key])) {
      console.log('Form has validation errors, preventing submission');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    // Find the selected organization
    const selectedOrg = organisationOptions.find(org => org.name === formData.organization_name);

    if (!selectedOrg) {
      console.error('Organization not found:', formData.organization_name);
      console.log('Available organizations:', organisationOptions.map(o => o.name));
      setValidationErrors((prev: Record<string, string>) => ({ ...prev, organization_name: 'Please select an organization' }));
      return;
    }

    // Use the organization name from the selected org object to ensure it matches exactly
    const organizationName = selectedOrg.name;
    
    // Debug logging
    console.log('Creating customer with organization:', {
      id: selectedOrg.id,
      name: organizationName,
      formDataValue: formData.organization_name
    });

    setIsSubmitting(true);
    
    try {
      const customer = await createQuoteDockCustomer({
        title: formData.title || undefined,
        first_name: formData.first_name || undefined,
        last_name: formData.last_name || undefined,
        phone_number: formData.phone_number,
        landline_number: formData.landline_number || undefined,
        email: formData.email || undefined,
        address: formData.address,
        billing_address: formData.billing_address || undefined,
        billing_email_address: formData.billing_email_address || undefined,
        billing_contact_name: formData.billing_contact_name || undefined,
        payment_preference: formData.payment_preference || undefined,
        vat_number: formData.vat_number || undefined,
        organization_id: selectedOrg.id,
        organization_name: organizationName  // Use the exact name from the selected org object
      });
      
      console.log('Customer created successfully:', {
        id: customer.id,
        organization_id: selectedOrg.id,
        organization_name: organizationName
      });

      // Clear all validation errors after successful creation
      setValidationErrors({});

      if (onCustomerCreated) {
        onCustomerCreated(customer);
      }
    } catch (err) {
      console.error('Failed to create customer:', err);
      
      // Handle specific error types
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      
      // Debug: Log the actual error message
      console.log('🔍 CustomerCreationForm error message:', errorMessage);
      
      if (isDirectoryPhoneDuplicateError(err) ||
          errorMessage === DIRECTORY_PHONE_DUPLICATE_MESSAGE ||
          errorMessage.includes('phone number already exists') || 
          errorMessage.includes('duplicate key value violates unique constraint') || 
          errorMessage.includes('idx_customers_phone_number') ||
          errorMessage.includes('idx_customers_phone_normalized') ||
          errorMessage.includes('409') ||
          errorMessage.includes('Conflict') ||
          errorMessage.includes('code: "23505"') ||
          errorMessage.includes('23505')) {
        setValidationErrors(prev => ({ ...prev, phone_number: DIRECTORY_PHONE_DUPLICATE_MESSAGE }));
      } else if (errorMessage.includes('email address already exists')) {
        setValidationErrors(prev => ({ ...prev, email: 'A customer with this email address already exists' }));
      } else if (errorMessage.includes('already exists with')) {
        // Generic duplicate error
        if (errorMessage.includes('phone number')) {
          setValidationErrors(prev => ({ ...prev, phone_number: DIRECTORY_PHONE_DUPLICATE_MESSAGE }));
        } else if (errorMessage.includes('email')) {
          setValidationErrors(prev => ({ ...prev, email: 'Email address already exists' }));
        }
      } else {
        // Show generic error
        setValidationErrors(prev => ({ ...prev, general: errorMessage }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Trigger debounced validation for phone number and email
    if (field === 'phone_number' || field === 'email') {
      debouncedValidation(field as 'phone_number' | 'email', value);
    }
  };

  const form = (
    <form onSubmit={handleSubmit}>
      <Stack spacing={compact ? 1.25 : 3}>
        <Box>
          {!compact && (
            <Typography level="title-sm" sx={{ mb: 1 }}>
              <IconWrapper icon={FaUser} />
              Customer Name
            </Typography>
          )}
          <Grid container spacing={compact ? 1 : 2}>
            <Grid xs={compact ? 3 : 12} sm={3}>
              <FormControl size={controlSize}>
                <FormLabel>Title</FormLabel>
                <Select
                  size={controlSize}
                  value={formData.title}
                  onChange={(_, value) => handleInputChange('title', value || '')}
                  placeholder="Title"
                >
                  <Option value="Mr">Mr</Option>
                  <Option value="Mrs">Mrs</Option>
                  <Option value="Ms">Ms</Option>
                  <Option value="Miss">Miss</Option>
                  <Option value="Dr">Dr</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={compact ? 4.5 : 12} sm={4.5}>
              <FormControl size={controlSize} error={!!validationErrors.first_name}>
                <FormLabel>First name</FormLabel>
                <Input
                  size={controlSize}
                  value={formData.first_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('first_name', e.target.value)}
                  placeholder="First name"
                  autoFocus={compact}
                />
                {validationErrors.first_name && (
                  <FormHelperText>{validationErrors.first_name}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid xs={compact ? 4.5 : 12} sm={4.5}>
              <FormControl size={controlSize} error={!!validationErrors.last_name}>
                <FormLabel>Last name</FormLabel>
                <Input
                  size={controlSize}
                  value={formData.last_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('last_name', e.target.value)}
                  placeholder="Last name"
                />
                {validationErrors.last_name && (
                  <FormHelperText>{validationErrors.last_name}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {!compact && <Divider />}

        <Box>
          {!compact && (
            <Typography level="title-sm" sx={{ mb: 1 }}>
              Contact Information
            </Typography>
          )}
          <Grid container spacing={compact ? 1 : 2}>
            <Grid xs={compact ? 6 : 12} sm={6}>
              <FormControl size={controlSize} error={!!validationErrors.phone_number}>
                <FormLabel>
                  {!compact && <IconWrapper icon={FaPhone} />}
                  Phone *
                </FormLabel>
                <Input
                  size={controlSize}
                  value={formData.phone_number}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('phone_number', e.target.value)}
                  placeholder="Phone number"
                  required
                />
                {validationErrors.phone_number && (
                  <FormHelperText>{validationErrors.phone_number}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {extrasOpen && (
              <Grid xs={12} sm={6}>
                <FormControl size={controlSize}>
                  <FormLabel>Landline</FormLabel>
                  <Input
                    size={controlSize}
                    value={formData.landline_number}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      if (value.length <= 20) {
                        handleInputChange('landline_number', value);
                      }
                    }}
                    placeholder="Landline"
                  />
                </FormControl>
              </Grid>
            )}
            <Grid xs={compact ? 6 : 12} sm={6}>
              <FormControl size={controlSize} error={!!validationErrors.email}>
                <FormLabel>
                  {!compact && <IconWrapper icon={FaEnvelope} />}
                  Email
                </FormLabel>
                <Input
                  size={controlSize}
                  type="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  placeholder="Email address"
                />
                {validationErrors.email && (
                  <FormHelperText>{validationErrors.email}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {!compact && <Divider />}

        <Box>
          {!compact && (
            <Typography level="title-sm" sx={{ mb: 1 }}>
              <IconWrapper icon={FaMapMarkerAlt} />
              Address Information
            </Typography>
          )}
          <FormControl size={controlSize} error={!!validationErrors.address}>
            <FormLabel>Address *</FormLabel>
            <Input
              size={controlSize}
              value={formData.address}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('address', e.target.value)}
              placeholder="Full address"
              required
            />
            {validationErrors.address && (
              <FormHelperText>{validationErrors.address}</FormHelperText>
            )}
          </FormControl>
          {extrasOpen && (
            <FormControl size={controlSize} sx={{ mt: compact ? 1.25 : 2 }}>
              <FormLabel>Billing address</FormLabel>
              <Input
                size={controlSize}
                value={formData.billing_address}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('billing_address', e.target.value)}
                placeholder="If different"
              />
            </FormControl>
          )}
        </Box>

        {extrasOpen && (
          <>
            {!compact && <Divider />}
            <Box>
              {!compact && (
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  <IconWrapper icon={FaCreditCard} />
                  Billing Information
                </Typography>
              )}
              <Grid container spacing={compact ? 1 : 2}>
                <Grid xs={12} sm={6}>
                  <FormControl size={controlSize}>
                    <FormLabel>Billing email</FormLabel>
                    <Input
                      size={controlSize}
                      type="email"
                      value={formData.billing_email_address}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('billing_email_address', e.target.value)}
                      placeholder="Billing email"
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl size={controlSize}>
                    <FormLabel>Billing contact</FormLabel>
                    <Input
                      size={controlSize}
                      value={formData.billing_contact_name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('billing_contact_name', e.target.value)}
                      placeholder="Billing contact"
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl size={controlSize}>
                    <FormLabel>Payment</FormLabel>
                    <Select
                      size={controlSize}
                      value={formData.payment_preference}
                      onChange={(_, value) => handleInputChange('payment_preference', value || '')}
                      placeholder="Preference"
                    >
                      <Option value="card">Card</Option>
                      <Option value="cash">Cash</Option>
                      <Option value="cheque">Cheque</Option>
                      <Option value="EFT">EFT</Option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl size={controlSize} error={!!validationErrors.vat_number}>
                    <FormLabel>VAT Number</FormLabel>
                    <Input
                      size={controlSize}
                      value={formData.vat_number}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('vat_number', e.target.value)}
                      placeholder="VAT number"
                    />
                    {validationErrors.vat_number && (
                      <FormHelperText>{validationErrors.vat_number}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {!compact && <Divider />}

        <FormControl size={controlSize} error={!!validationErrors.organization_name}>
          <FormLabel>
            {!compact && <IconWrapper icon={FaBuilding} />}
            Organization *
          </FormLabel>
          <Select
            size={controlSize}
            value={formData.organization_name}
            onChange={(_, value) => handleInputChange('organization_name', value || '')}
            placeholder="Select organization"
            required
          >
            {organisationOptions.map((org) => (
              <Option key={org.id} value={org.name}>
                {org.name}
              </Option>
            ))}
          </Select>
          {validationErrors.organization_name && (
            <FormHelperText>{validationErrors.organization_name}</FormHelperText>
          )}
        </FormControl>

        {compact && (
          <Button
            type="button"
            variant="plain"
            size="sm"
            onClick={() => setShowMore((open) => !open)}
            sx={{ alignSelf: 'flex-start', px: 0 }}
          >
            {showMore ? 'Fewer details' : 'More details'}
          </Button>
        )}

        <Box sx={{ display: 'flex', gap: compact ? 1 : 2, justifyContent: 'flex-end', pt: compact ? 0.5 : 0 }}>
          {onCancel && !compact && (
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            size={controlSize}
            loading={loading || isSubmitting}
            disabled={loading || isSubmitting}
            startDecorator={!compact ? <IconWrapper icon={FaSave} /> : undefined}
            fullWidth={compact}
          >
            {isSubmitting ? 'Creating...' : compact ? 'Add customer' : 'Create Customer'}
          </Button>
        </Box>
      </Stack>
    </form>
  );

  const alerts = (
    <>
      {error && (
        <Alert color="danger" sx={{ mb: compact ? 1 : 2 }}>
          {error}
        </Alert>
      )}
      {validationErrors.general && (
        <Alert color="danger" sx={{ mb: compact ? 1 : 2 }}>
          {validationErrors.general}
        </Alert>
      )}
    </>
  );

  if (compact) {
    return (
      <Box>
        {alerts}
        {form}
      </Box>
    );
  }

  return (
    <Card sx={{ maxWidth: 600, mx: 'auto' }}>
      <CardContent>
        <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconWrapper icon={FaUser} />
          Create New Customer
        </Typography>
        {alerts}
        {form}
      </CardContent>
    </Card>
  );
};

export default CustomerCreationForm; 